/**
 * Helpers for client-portal invoice rendering.
 *
 * Extracted from `pages/client/Invoices.jsx` so they're independently testable
 * (the original inline closures couldn't be unit-tested directly).
 */

const TIME_BASED_OVERDUE_CANDIDATE_STATUSES = [
  'open',
  'ready_for_payment',
  'amended_ready_for_payment',
];

const PAYABLE_STATUSES = [
  'open',
  'ready_for_payment',
  'amended_ready_for_payment',
  'payment_processing',
  // Literal 'overdue' status is set by the admin overdue-reminder cron when a
  // reminder is sent. The backend (payments.py) already accepts this status
  // for payment; the frontend must too, otherwise the Pay button vanishes
  // on invoices that have been "officially" marked overdue.
  'overdue',
];

/**
 * `true` if the invoice should be rendered with the red Overdue treatment.
 *
 * Two paths qualify:
 *   1. Backend has explicitly flipped `status` to `"overdue"` (admin cron).
 *   2. Status is still in a payable state but the due_date has passed (we
 *      haven't run the cron yet, but UI should still show urgency).
 */
export const isOverdue = (invoice) => {
  if (!invoice) return false;
  if (invoice.status === 'overdue') return true;
  if (!TIME_BASED_OVERDUE_CANDIDATE_STATUSES.includes(invoice.status)) return false;
  if (!invoice.due_date) return false;
  return new Date(invoice.due_date) < new Date();
};

/**
 * `true` if the client should be allowed to start a payment on this invoice.
 */
export const canPay = (invoice) => {
  if (!invoice) return false;
  if (PAYABLE_STATUSES.includes(invoice.status)) return true;
  if (isOverdue(invoice)) return true;
  if (invoice.payment_failed) return true;
  return false;
};

/**
 * Returns the visual status to render — collapses the time-based overdue
 * case into the literal 'overdue' badge.
 */
export const getEffectiveStatus = (invoice) => {
  if (isOverdue(invoice)) return 'overdue';
  return invoice?.status;
};
